import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "../providers/supabase/supabase";

export type OutreachTimelineEntry = {
  id: string;
  kind: "sent" | "reply" | "opt_out" | "unsubscribe";
  occurredAt: string;
  title: string;
  detail: string | null;
  sendingIdentity: string | null;
};

/**
 * Merges mentorax.outreach_events, email_replies, opt_outs, and unsubscribes
 * for one contact into a single chronological feed, with sending_identity
 * joined in via mentorax.outreach_event_identity (a companion table, not
 * columns on outreach_events itself — see PRD "Data model" section).
 *
 * Queries the mentorax schema directly via the Supabase client rather than
 * through the react-admin data provider (which is scoped to `public`),
 * since these four tables live in a different schema. DR-20260830-01
 * granted `authenticated` read-only SELECT on exactly these five tables.
 */
export function useContactOutreachTimeline(contactId: number | undefined) {
  return useQuery({
    queryKey: ["contact-outreach-timeline", contactId],
    enabled: !!contactId,
    queryFn: async (): Promise<OutreachTimelineEntry[]> => {
      const client = getSupabaseClient();
      const mentorax = client.schema("mentorax");

      const [eventsRes, repliesRes, optOutsRes, unsubsRes] =
        await Promise.all([
          mentorax
            .from("outreach_events")
            .select("id, event_type, status, occurred_at, summary")
            .eq("contact_id", contactId),
          mentorax
            .from("email_replies")
            .select(
              "id, reply_type, sentiment, reply_summary, received_at, outreach_event_id",
            )
            .eq("contact_id", contactId),
          mentorax
            .from("opt_outs")
            .select("id, channel, opt_out_type, reason, occurred_at")
            .eq("contact_id", contactId),
          mentorax
            .from("unsubscribes")
            .select("id, unsubscribe_scope, unsubscribed_at")
            .eq("contact_id", contactId),
        ]);

      for (const res of [eventsRes, repliesRes, optOutsRes, unsubsRes]) {
        if (res.error) throw res.error;
      }

      const events = eventsRes.data ?? [];
      const eventIds = events.map((e) => e.id);

      const identityByEventId = new Map<string, string>();
      if (eventIds.length > 0) {
        const { data: identities, error } = await mentorax
          .from("outreach_event_identity")
          .select("outreach_event_id, sending_identity")
          .in("outreach_event_id", eventIds);
        if (error) throw error;
        for (const row of identities ?? []) {
          if (row.outreach_event_id && row.sending_identity) {
            identityByEventId.set(row.outreach_event_id, row.sending_identity);
          }
        }
      }

      const entries: OutreachTimelineEntry[] = [
        ...events.map((e) => ({
          id: `event-${e.id}`,
          kind: "sent" as const,
          occurredAt: e.occurred_at,
          title: e.event_type,
          detail: e.summary ?? e.status ?? null,
          sendingIdentity: identityByEventId.get(e.id) ?? null,
        })),
        ...(repliesRes.data ?? []).map((r) => ({
          id: `reply-${r.id}`,
          kind: "reply" as const,
          occurredAt: r.received_at,
          title: r.reply_type ?? "Reply",
          detail: r.reply_summary ?? r.sentiment ?? null,
          sendingIdentity: r.outreach_event_id
            ? (identityByEventId.get(r.outreach_event_id) ?? null)
            : null,
        })),
        ...(optOutsRes.data ?? []).map((o) => ({
          id: `optout-${o.id}`,
          kind: "opt_out" as const,
          occurredAt: o.occurred_at,
          title: `Opted out — ${o.channel}`,
          detail: o.reason ?? o.opt_out_type ?? null,
          sendingIdentity: null,
        })),
        ...(unsubsRes.data ?? []).map((u) => ({
          id: `unsub-${u.id}`,
          kind: "unsubscribe" as const,
          occurredAt: u.unsubscribed_at,
          title: "Unsubscribed",
          detail: u.unsubscribe_scope ?? null,
          sendingIdentity: null,
        })),
      ];

      return entries.sort(
        (a, b) =>
          new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
      );
    },
  });
}
